import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import type { Counterparty } from "../entities/counterparties";
import { requestErrorText } from "./counterpartiesSupport";
import type { ShellServices } from "./services";
import { counterpartyKeys } from "./useCounterpartyNames";
import { useDebouncedValue } from "./useDebouncedValue";
import { Notice } from "./Notice";

type CounterpartyService = NonNullable<ShellServices["counterparties"]>;

const PAGE_SIZE = 25;

function CounterpartyOption({
  counterparty,
  selected,
  onToggleContact,
  onToggleAll,
}: {
  counterparty: Counterparty;
  selected: ReadonlyMap<string, string>;
  onToggleContact: (id: string, email: string) => void;
  onToggleAll: (counterparty: Counterparty) => void;
}) {
  const checkbox = useRef<HTMLInputElement>(null);
  const chosen = counterparty.contacts.filter((contact) =>
    selected.has(contact.id),
  ).length;
  const all =
    counterparty.contacts.length > 0 && chosen === counterparty.contacts.length;
  useEffect(() => {
    // Частичный выбор показывается «чёрточкой»: у контрагента могут быть
    // контакты, которым это письмо отправлять не нужно.
    if (checkbox.current) checkbox.current.indeterminate = chosen > 0 && !all;
  }, [chosen, all]);

  return (
    <li className="cp-picker__item">
      <label className="cp-check">
        <input
          ref={checkbox}
          type="checkbox"
          checked={all}
          disabled={counterparty.contacts.length === 0}
          onChange={() => onToggleAll(counterparty)}
        />
        <span>
          {counterparty.name}
          {counterparty.contacts.length === 0 ? (
            <small className="cp-hint">Нет контактов с email</small>
          ) : null}
        </span>
      </label>
      {counterparty.contacts.length === 0 ? null : (
        <ul
          className="cp-picker__contacts"
          aria-label={`Контакты: ${counterparty.name}`}
        >
          {counterparty.contacts.map((contact) => (
            <li key={contact.id}>
              <label className="cp-check">
                <input
                  type="checkbox"
                  checked={selected.has(contact.id)}
                  onChange={() => onToggleContact(contact.id, contact.email)}
                />
                <span>
                  {contact.email}
                  {contact.name || contact.position ? (
                    <small className="cp-hint">
                      {[contact.name, contact.position]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Выбор сохранённых контрагентов для поля «Кому». Можно отметить несколько
 * контрагентов сразу (или отдельные их контакты); их email добавляются к тому,
 * что уже введено в поле, ничего не стирая.
 */
export function CounterpartyRecipientPicker({
  service,
  onApply,
  disabled = false,
}: {
  service: CounterpartyService;
  /** Получает адреса, которые нужно добавить к полю. */
  onApply: (emails: string[]) => void;
  disabled?: boolean;
}) {
  const panelId = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  const [announcement, setAnnouncement] = useState("");

  const debounced = useDebouncedValue(search.trim());

  const list = useInfiniteQuery({
    queryKey: [...counterpartyKeys.all, "recipients", debounced],
    initialPageParam: 1,
    enabled: open,
    queryFn: ({ pageParam }) =>
      service.list({
        page: pageParam,
        per_page: PAGE_SIZE,
        ...(debounced === "" ? {} : { search: debounced }),
      }),
    getNextPageParam: (last) =>
      last.meta.current_page < last.meta.last_page
        ? last.meta.current_page + 1
        : undefined,
  });
  const items = useMemo(
    () => list.data?.pages.flatMap((page) => page.data) ?? [],
    [list.data],
  );

  function toggleContact(id: string, email: string) {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(id)) next.delete(id);
      else next.set(id, email);
      return next;
    });
  }

  function toggleAll(counterparty: Counterparty) {
    setSelected((current) => {
      const next = new Map(current);
      const allChosen = counterparty.contacts.every((c) => next.has(c.id));
      for (const contact of counterparty.contacts) {
        if (allChosen) next.delete(contact.id);
        else next.set(contact.id, contact.email);
      }
      return next;
    });
  }

  function apply() {
    const emails = [...new Set(selected.values())];
    if (emails.length === 0) return;
    onApply(emails);
    setAnnouncement(`Добавлено адресов: ${emails.length}`);
    setSelected(new Map());
    setOpen(false);
    toggle.current?.focus();
  }

  return (
    <div className="cp-picker">
      <button
        ref={toggle}
        type="button"
        className="button button--secondary button--compact"
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        Контрагенты
      </button>
      <span className="visually-hidden" role="status">
        {announcement}
      </span>

      {open ? (
        <div
          id={panelId}
          className="cp-picker__panel"
          role="group"
          aria-label="Сохранённые контрагенты"
        >
          <div className="field cp-field">
            <label htmlFor={`${panelId}-search`}>
              <span>Поиск по названию или ИНН</span>
            </label>
            <input
              id={`${panelId}-search`}
              type="search"
              value={search}
              maxLength={255}
              autoComplete="off"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          {list.isLoading ? (
            <p aria-busy="true">Загружаем контрагентов…</p>
          ) : null}
          {list.isError ? (
            <Notice>
              {requestErrorText(
                list.error,
                "Не удалось загрузить контрагентов.",
              )}{" "}
              <button
                type="button"
                className="button button--text button--compact"
                onClick={() => void list.refetch()}
              >
                Повторить
              </button>
            </Notice>
          ) : null}
          {list.isSuccess && items.length === 0 ? (
            <p className="muted">
              {debounced === ""
                ? "Сохранённых контрагентов пока нет. Их можно добавить в разделе «Контрагенты»."
                : `По запросу «${debounced}» ничего не найдено.`}
            </p>
          ) : null}
          {items.length > 0 ? (
            <ul className="cp-picker__list">
              {items.map((counterparty) => (
                <CounterpartyOption
                  key={counterparty.id}
                  counterparty={counterparty}
                  selected={selected}
                  onToggleContact={toggleContact}
                  onToggleAll={toggleAll}
                />
              ))}
            </ul>
          ) : null}
          {list.hasNextPage ? (
            <button
              type="button"
              className="button button--text button--compact"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {list.isFetchingNextPage ? "Загружаем…" : "Показать ещё"}
            </button>
          ) : null}

          <div className="cp-picker__footer">
            <span className="muted" aria-live="polite">
              Выбрано адресов: {new Set(selected.values()).size}
            </span>
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={selected.size === 0}
              onClick={apply}
            >
              Добавить в «Кому»
            </button>
            <button
              type="button"
              className="button button--text button--compact"
              onClick={() => {
                setOpen(false);
                toggle.current?.focus();
              }}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
